import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, parse } from 'date-fns';
import PageLayout from '@/components/PageLayout';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useCar } from '@/contexts/CarContext';
import { useExpenseCategories } from '@/contexts/ExpenseCategoriesContext';
import { useAuth } from '@/contexts/AuthProvider';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon, Edit, Trash2, Eye, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import { toast } from '@/components/ui/use-toast';

interface Expense {
  id: string;
  expense_date: string;
  category: string;
  amount_cents: number;
  odometer_reading: number | null;
  receipt_url: string | null;
  description: string | null;
  fuel_volume_liters: number | null;
  type: 'expense' | 'service'; // Add type to distinguish
}

interface ServiceEntry {
  id: string;
  service_date: string;
  total_cents: number;
  type: 'service';
}

type RecurringRule = {
  id: string;
  car_id: string;
  user_id: string;
  category: string;
  amount_cents: number;
  description: string | null;
  start_date: string;
  end_date: string;
  repeat_interval: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly';
  receipt_url: string | null;
  created_at?: string;
  updated_at?: string;
};

const REPEAT_LABELS: Record<RecurringRule['repeat_interval'], string> = {
  daily: 'addCost.recurringRepeatOptions.everyDay',
  weekly: 'addCost.recurringRepeatOptions.everyWeek',
  biweekly: 'addCost.recurringRepeatOptions.every2Weeks',
  monthly: 'addCost.recurringRepeatOptions.everyMonth',
  quarterly: 'addCost.recurringRepeatOptions.everyQuarter',
  yearly: 'addCost.recurringRepeatOptions.everyYear',
};

const ExpensesListPage: React.FC = () => {
  const { carId } = useParams<{ carId: string }>();
  const navigate = useNavigate();
  const { t, formatCurrency } = useI18n();
  const { cars } = useCar();
  const { user } = useAuth();
  const expenseCategoriesResult = useExpenseCategories?.();
  const { categories: dbCategories = [] } = expenseCategoriesResult || {};
  const sortedCategories = useMemo(
    () =>
      [...dbCategories].sort((a, b) => {
        const labelA = (t(`category.${a.value}`) as string) || a.label || a.value;
        const labelB = (t(`category.${b.value}`) as string) || b.label || b.value;
        return labelA.localeCompare(labelB);
      }),
    [dbCategories, t]
  );
  
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [serviceEntries, setServiceEntries] = useState<ServiceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);
  
  // Filter states
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]); // 0-11
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);

  // Tab: Past expenses | Recurring expenses
  const [activeExpensesTab, setActiveExpensesTab] = useState<'past' | 'recurring'>('past');
  const [recurringRules, setRecurringRules] = useState<RecurringRule[]>([]);
  const [loadingRecurring, setLoadingRecurring] = useState(false);
  const [editRuleDialog, setEditRuleDialog] = useState<RecurringRule | null>(null);
  const [deleteRuleTarget, setDeleteRuleTarget] = useState<RecurringRule | null>(null);
  const [editRuleForm, setEditRuleForm] = useState<{ amount_cents: string; description: string; end_date: string; repeat_interval: RecurringRule['repeat_interval'] }>({ amount_cents: '', description: '', end_date: '', repeat_interval: 'monthly' });
  const [savingRule, setSavingRule] = useState(false);

  const car = cars.find(c => c.id === carId);

  // Combine expenses and service entries, apply filters, and group by month
  const allItems = useMemo(() => {
    const combined: (Expense | ServiceEntry)[] = [
      ...expenses.map(e => ({ ...e, type: 'expense' as const })),
      ...serviceEntries.map(s => ({ ...s, type: 'service' as const }))
    ];
    
    // Apply category filter (multi-select)
    let filtered = combined;
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(item => {
        if (item.type === 'service') return selectedCategories.includes('Service');
        return selectedCategories.includes((item as Expense).category);
      });
    }
    
    // Apply month/year filters
    if (selectedMonths.length > 0 || selectedYears.length > 0) {
      filtered = filtered.filter(item => {
        const rawDate =
          item.type === 'expense'
            ? (item as Expense).expense_date
            : (item as ServiceEntry).service_date;
        const d = new Date(rawDate);
        const month = d.getMonth();
        const year = d.getFullYear().toString();

        const monthOk =
          selectedMonths.length === 0 || selectedMonths.includes(month);
        const yearOk =
          selectedYears.length === 0 || selectedYears.includes(year);

        return monthOk && yearOk;
      });
    }
    
    return filtered;
  }, [expenses, serviceEntries, selectedCategories, selectedMonths, selectedYears]);

  // Compute which years have any data (for year pills)
  const availableYears = useMemo(() => {
    const years = new Set<string>();
    expenses.forEach((e) => {
      const y = new Date(e.expense_date).getFullYear();
      if (!Number.isNaN(y)) years.add(y.toString());
    });
    serviceEntries.forEach((s) => {
      const y = new Date(s.service_date).getFullYear();
      if (!Number.isNaN(y)) years.add(y.toString());
    });
    return Array.from(years).sort();
  }, [expenses, serviceEntries]);

  // Group by month
  const itemsByMonth = useMemo(() => {
    const grouped: Record<string, (Expense | ServiceEntry)[]> = {};
    allItems.forEach(item => {
      const date = item.type === 'expense' ? (item as Expense).expense_date : (item as ServiceEntry).service_date;
      const monthKey = format(new Date(date), 'MMMM yyyy');
      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(item);
    });
    return grouped;
  }, [allItems]);

  // Calculate monthly subtotals
  const monthlyTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    Object.entries(itemsByMonth).forEach(([monthKey, items]) => {
      const total = items.reduce((sum, item) => {
        const amount = item.type === 'expense' 
          ? (item as Expense).amount_cents 
          : (item as ServiceEntry).total_cents;
        return sum + (amount || 0);
      }, 0);
      totals[monthKey] = total / 100; // Convert cents to currency
    });
    return totals;
  }, [itemsByMonth]);

  // Get category icon
  const getCategoryIcon = (category: string) => {
    const categoryData = dbCategories.find(c => c.value === category);
    return categoryData?.icon_path || null;
  };

  useEffect(() => {
    if (!carId) return;

    const loadExpenses = async () => {
      setLoading(true);
      try {
        // Get all expense_ids from service entries to exclude them
        const { data: serviceEntriesWithExpenses } = await supabase
          .from('service_entries')
          .select('expense_ids')
          .eq('car_id', carId);
        
        const excludedExpenseIds: string[] = [];
        (serviceEntriesWithExpenses || []).forEach((entry: any) => {
          if (entry.expense_ids && Array.isArray(entry.expense_ids) && entry.expense_ids.length > 0) {
            excludedExpenseIds.push(...entry.expense_ids);
          }
        });

        // Fetch all expenses
        const { data: allExpensesData, error } = await supabase
          .from('expenses')
          .select('id, expense_date, category, amount_cents, odometer_reading, receipt_url, description, fuel_volume_liters')
          .eq('car_id', carId)
          .is('deleted_at', null)
          .order('expense_date', { ascending: false });

        if (error) throw error;

        // Filter out expenses linked to service entries and service expenses
        const excludedSet = new Set(excludedExpenseIds);
        const filteredExpenses = (allExpensesData || []).filter((expense: any) => {
          if (excludedSet.has(expense.id)) {
            return false;
          }
          if (expense.category === 'Other' && expense.description && expense.description.startsWith('Service:')) {
            return false;
          }
          return true;
        });

        setExpenses(filteredExpenses as Expense[]);

        // Fetch service entries
        const { data: serviceData, error: serviceError } = await supabase
          .from('service_entries')
          .select('id, service_date, total_cents')
          .eq('car_id', carId)
          .not('total_cents', 'is', null)
          .order('service_date', { ascending: false });

        if (serviceError) {
          console.error('Error loading service entries:', serviceError);
        } else {
          setServiceEntries((serviceData || []) as ServiceEntry[]);
        }
      } catch (error) {
        console.error('Error loading expenses:', error);
        toast({
          title: t('common.error') || 'Error',
          description: t('expenses.loadExpensesFailed') || t('common.loadFailed') || 'Failed to load expenses',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadExpenses();
  }, [carId, t]);

  // Load recurring expense rules when Recurring tab is active (all users for this car)
  useEffect(() => {
    if (!carId || activeExpensesTab !== 'recurring') {
      setRecurringRules([]);
      return;
    }
    const loadRecurring = async () => {
      setLoadingRecurring(true);
      try {
        const { data, error } = await supabase
          .from('recurring_expense_rules')
          .select('id, car_id, user_id, category, amount_cents, description, start_date, end_date, repeat_interval, receipt_url, created_at, updated_at')
          .eq('car_id', carId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setRecurringRules((data || []) as RecurringRule[]);
      } catch (e) {
        console.error('Error loading recurring rules:', e);
        setRecurringRules([]);
      } finally {
        setLoadingRecurring(false);
      }
    };
    loadRecurring();
  }, [carId, activeExpensesTab]);

  // Start of tomorrow UTC for "future" expenses
  const startOfTomorrowUtc = () => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + 1);
    d.setUTCHours(0, 0, 0, 0);
    return d.toISOString();
  };

  // Start of today UTC (for edit: delete from today so materialize can recreate)
  const startOfTodayUtc = () => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    return d.toISOString();
  };

  const handleDeleteRecurringRule = async () => {
    if (!deleteRuleTarget) return;
    try {
      const tomorrowStart = startOfTomorrowUtc();
      await supabase
        .from('expenses')
        .update({ deleted_at: new Date().toISOString() })
        .eq('recurring_rule_id', deleteRuleTarget.id)
        .gte('expense_date', tomorrowStart);
      const { error } = await supabase
        .from('recurring_expense_rules')
        .delete()
        .eq('id', deleteRuleTarget.id);
      if (error) throw error;
      setRecurringRules(prev => prev.filter(r => r.id !== deleteRuleTarget.id));
      setDeleteRuleTarget(null);
      toast({
        title: t('common.success') || 'Success',
        description: t('expenses.recurring.deleted') || 'Recurring expense deleted. Future entries have been removed.',
      });
    } catch (e) {
      console.error('Error deleting recurring rule:', e);
      toast({
        title: t('common.error') || 'Error',
        description: t('expenses.recurring.deleteFailed') || 'Failed to delete recurring expense',
        variant: 'destructive',
      });
    }
  };

  const openEditRule = (rule: RecurringRule) => {
    setEditRuleDialog(rule);
    setEditRuleForm({
      amount_cents: (rule.amount_cents / 100).toString(),
      description: rule.description || '',
      end_date: rule.end_date,
      repeat_interval: rule.repeat_interval,
    });
  };

  const handleSaveEditRule = async () => {
    if (!editRuleDialog) return;
    const amountCents = Math.round(parseFloat(editRuleForm.amount_cents || '0') * 100);
    if (amountCents <= 0) {
      toast({ title: t('common.error') || 'Error', description: t('expenses.invalidAmount') || 'Invalid amount', variant: 'destructive' });
      return;
    }
    setSavingRule(true);
    try {
      const { error: updateError } = await supabase
        .from('recurring_expense_rules')
        .update({
          amount_cents: amountCents,
          description: editRuleForm.description || null,
          end_date: editRuleForm.end_date,
          repeat_interval: editRuleForm.repeat_interval,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editRuleDialog.id);
      if (updateError) throw updateError;
      const todayStart = startOfTodayUtc();
      await supabase
        .from('expenses')
        .update({ deleted_at: new Date().toISOString() })
        .eq('recurring_rule_id', editRuleDialog.id)
        .gte('expense_date', todayStart);
      setRecurringRules(prev => prev.map(r => r.id === editRuleDialog.id ? { ...r, amount_cents: amountCents, description: editRuleForm.description || null, end_date: editRuleForm.end_date, repeat_interval: editRuleForm.repeat_interval } : r));
      setEditRuleDialog(null);
      toast({
        title: t('common.success') || 'Success',
        description: t('expenses.recurring.updated') || 'Recurring expense updated. Future entries will be recreated with the new values.',
      });
    } catch (e) {
      console.error('Error updating recurring rule:', e);
      toast({
        title: t('common.error') || 'Error',
        description: t('expenses.recurring.updateFailed') || 'Failed to update recurring expense',
        variant: 'destructive',
      });
    } finally {
      setSavingRule(false);
    }
  };

  const handleViewPhoto = async (expense: Expense) => {
    if (!expense.receipt_url) return;
    
    try {
      const signedUrlInfo = await getSignedViewUrl(expense.receipt_url);
      setPhotoUrl(signedUrlInfo.url);
      setSelectedExpense(expense);
      setPhotoDialogOpen(true);
    } catch (error) {
      console.error('Error loading photo:', error);
      toast({
        title: t('common.error') || 'Error',
        description: t('expenses.loadPhotoFailed') || t('common.loadFailed') || 'Failed to load photo',
        variant: 'destructive',
      });
    }
  };

  const handleOpenExpense = (item: Expense | ServiceEntry) => {
    if (item.type === 'service') {
      // Open full Service Entry view
      const serviceId = (item as ServiceEntry).id;
      navigate(`/services/view/${serviceId}?tab=repairs&carId=${carId}`);
    } else {
      // Navigate to expense view page
      navigate(`/expenses/view/${item.id}?carId=${carId}`);
    }
  };

  const handleEditExpense = (expense: Expense) => {
    navigate(`/expenses/edit/${expense.id}?carId=${carId}`);
  };

  const handleDeleteClick = (expense: Expense) => {
    setExpenseToDelete(expense);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!expenseToDelete) return;

    try {
      const { error } = await supabase
        .from('expenses')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', expenseToDelete.id);

      if (error) throw error;

      setExpenses(prev => prev.filter(e => e.id !== expenseToDelete.id));
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
      
      toast({
        title: t('common.success') || 'Success',
        description: t('common.deleted') || 'Expense deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast({
        title: t('common.error') || 'Error',
        description: t('common.deleteFailed') || 'Failed to delete expense',
        variant: 'destructive',
      });
    }
  };

  const getCategoryLabel = (category: string) => {
    return t(`category.${category}`) || category;
  };

  const toggleCategory = (value: string) => {
    setSelectedCategories(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const toggleMonth = (value: number) => {
    setSelectedMonths((prev) =>
      prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]
    );
  };

  const toggleYearFilter = (value: string) => {
    setSelectedYears((prev) =>
      prev.includes(value) ? prev.filter((y) => y !== value) : [...prev, value]
    );
  };

  // Check if any filters are active
  const hasActiveFilters =
    selectedCategories.length > 0 ||
    selectedMonths.length > 0 ||
    selectedYears.length > 0;

  return (
    <PageLayout
      title={t('expenses.list') || 'Expenses'}
      showBackButton
      onBack={() => navigate(`/cars/${carId}?tab=costs`)}
      className="bg-[#F5F5F5]"
    >
      <div className="min-h-screen font-lato text-black">
        {loading ? (
          <div className="text-center py-8 text-gray-500 p-4">
            {t('common.loading') || 'Loading...'}
          </div>
        ) : (
          <div className="p-4">
            {/* Tab switch: Past expenses | Recurring expenses */}
            <Tabs value={activeExpensesTab} onValueChange={(v) => setActiveExpensesTab(v as 'past' | 'recurring')} className="w-full mb-4">
              <TabsList className="w-full grid grid-cols-2 p-0 m-0 rounded-lg bg-gray-100 gap-1">
                <TabsTrigger value="past" className="w-full h-9 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow flex items-center justify-center">
                  {t('expenses.tabs.past') || 'Past expenses'}
                </TabsTrigger>
                <TabsTrigger value="recurring" className="w-full h-9 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow flex items-center justify-center">
                  {t('expenses.tabs.recurring') || 'Recurring expenses'}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {activeExpensesTab === 'recurring' ? (
              /* Recurring expenses list */
              loadingRecurring ? (
                <div className="text-center py-8 text-gray-500">{t('common.loading') || 'Loading...'}</div>
              ) : recurringRules.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 mb-4">{t('expenses.recurring.empty') || 'No recurring expenses. Add one from Add expense when you create a cost with a recurring schedule.'}</p>
                  <Button onClick={() => navigate(`/expenses/add?carId=${carId}`)} className="bg-[#E7FE52] text-black hover:opacity-90">
                    {t('addCost.addExpense') || t('actions.add') || 'Add Expense'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {recurringRules.map((rule) => {
                    const categoryLabel = getCategoryLabel(rule.category);
                    const repeatLabel = t(REPEAT_LABELS[rule.repeat_interval]) || rule.repeat_interval;
                    return (
                      <div
                        key={rule.id}
                        className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm truncate">{rule.description || categoryLabel}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {categoryLabel} • {repeatLabel} • {format(new Date(rule.start_date), 'dd MMM yyyy')} – {format(new Date(rule.end_date), 'dd MMM yyyy')}
                          </p>
                          <p className="font-semibold text-sm text-gray-900 mt-1">{formatCurrency(rule.amount_cents / 100)}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-lg"
                            onClick={() => openEditRule(rule)}
                            aria-label={t('actions.edit') || 'Edit'}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-lg"
                            onClick={() => setDeleteRuleTarget(rule)}
                            aria-label={t('actions.delete') || 'Delete'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
            <>
            {/* Filter Button - styled similarly to RecordsPage */}
            <div className="mb-4 flex justify-end">
              <Button
                onClick={() => setFilterDialogOpen(true)}
                variant="secondary"
                size="sm"
                className="flex items-center justify-center w-8 h-8 border border-input bg-white shadow rounded-xl"
                aria-label={t('expenses.filters') || 'Filters'}
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="2" y="3" width="12" height="2" rx="1" fill="currentColor" />
                  <rect x="2" y="7" width="8" height="2" rx="1" fill="currentColor" />
                  <rect x="2" y="11" width="4" height="2" rx="1" fill="currentColor" />
                </svg>
                {hasActiveFilters && (
                  <span className="ml-1 bg-[#E7FE52] text-black rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-semibold">
                    !
                  </span>
                )}
              </Button>
            </div>

            {allItems.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">{t('expenses.noExpenses') || 'No expenses found'}</p>
                <Button
                  onClick={() => navigate(`/expenses/add?carId=${carId}`)}
                  className="bg-[#E7FE52] text-black hover:opacity-90"
                >
                  {t('addCost.addExpense') || t('actions.add') || 'Add Expense'}
                </Button>
              </div>
            ) : (
              <>
                {/* Month Groups */}
            {Object.entries(itemsByMonth)
              .sort(([a], [b]) => {
                // Sort months descending (newest first)
                const dateA = new Date(a);
                const dateB = new Date(b);
                return dateB.getTime() - dateA.getTime();
              })
              .map(([monthKey, monthItems]) => {
                const monthTotal = monthlyTotals[monthKey] || 0;
                
                return (
                  <section key={monthKey} className="mb-6">
                    <div className="flex items-center justify-between mb-2 px-1">
                      <h2 className="text-sm font-semibold text-gray-600">{monthKey}</h2>
                      <p className="text-sm font-semibold text-gray-900">
                        {t('expenses.subtotal') || 'Subtotal'}: {formatCurrency(monthTotal)}
                      </p>
                    </div>
                  
                    {monthItems.map((item) => {
                      const isService = item.type === 'service';
                      const expense = isService ? null : (item as Expense);
                      const serviceEntry = isService ? (item as ServiceEntry) : null;
                      
                      const date = isService ? serviceEntry!.service_date : expense!.expense_date;
                      const dayMonth = format(new Date(date), 'd MMM');
                      const categoryLabel = isService ? (t('category.Service') || 'Service') : getCategoryLabel(expense!.category);
                      const categoryIcon = isService ? null : getCategoryIcon(expense!.category);
                      const amount = isService ? serviceEntry!.total_cents : expense!.amount_cents;
                      const odometerReading = isService ? null : expense!.odometer_reading;
                      const receiptUrl = isService ? null : expense!.receipt_url;
                      const fuelVolume = isService ? null : expense!.fuel_volume_liters;
                      const description = isService ? null : expense!.description;
                      
                      return (
                        <div
                          key={item.id}
                          className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 mb-3 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => handleOpenExpense(item)}
                        >
                          {/* Left side */}
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {/* Category icon */}
                            {isService ? (
                              <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                                <img
                                  src="/icons/AddServiceEntry/Repair.png"
                                  alt={categoryLabel}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            ) : categoryIcon ? (
                              <div className="w-8 h-8 rounded-full overflow-hidden border border-white bg-gray-200 flex-shrink-0">
                                <img 
                                  src={categoryIcon} 
                                  alt={categoryLabel}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                                <span className="text-xs text-gray-500">
                                  {categoryLabel.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}

                            {/* Details */}
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-sm truncate">
                                {description || categoryLabel}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {dayMonth} • {odometerReading !== null ? `${odometerReading.toLocaleString()} km` : categoryLabel}
                              </p>
                            </div>
                          </div>

                          {/* Right side: Amount, Fuel Volume, and Receipt Icon */}
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <div className="flex items-center gap-2">
                              {receiptUrl && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (expense) handleViewPhoto(expense);
                                  }}
                                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                  aria-label={t('expenses.viewReceipt') || 'View receipt'}
                                >
                                  <ImageIcon className="w-4 h-4 text-gray-500" />
                                </button>
                              )}
                              <p className="font-semibold text-sm whitespace-nowrap">
                                {formatCurrency(amount / 100)}
                              </p>
                            </div>
                            {fuelVolume !== null && fuelVolume > 0 && (
                              <p className="text-xs text-gray-500 whitespace-nowrap">
                                {fuelVolume.toFixed(3)} L
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </section>
                );
              })}
              </>
            )}
            </>
            )}
          </div>
        )}
      </div>

      {/* Edit recurring rule dialog */}
      <Dialog open={!!editRuleDialog} onOpenChange={(open) => !open && setEditRuleDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('expenses.recurring.editTitle') || 'Edit recurring expense'}</DialogTitle>
          </DialogHeader>
          {editRuleDialog && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm text-gray-700">{t('expenses.amount') || 'Amount'}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editRuleForm.amount_cents}
                  onChange={(e) => setEditRuleForm(f => ({ ...f, amount_cents: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-gray-700">{t('expenses.description') || 'Description'}</Label>
                <Input
                  value={editRuleForm.description}
                  onChange={(e) => setEditRuleForm(f => ({ ...f, description: e.target.value }))}
                  className="mt-1"
                  placeholder={t('common.optionalField') || '(optional)'}
                />
              </div>
              <div>
                <Label className="text-sm text-gray-700">{t('addCost.recurringEnds') || 'Ends'}</Label>
                <Input
                  type="date"
                  value={editRuleForm.end_date}
                  onChange={(e) => setEditRuleForm(f => ({ ...f, end_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-gray-700">{t('addCost.recurringRepeat') || 'Repeat'}</Label>
                <Select value={editRuleForm.repeat_interval} onValueChange={(v: RecurringRule['repeat_interval']) => setEditRuleForm(f => ({ ...f, repeat_interval: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['daily', 'weekly', 'biweekly', 'monthly', 'quarterly', 'yearly'] as const).map((interval) => (
                      <SelectItem key={interval} value={interval}>
                        {t(REPEAT_LABELS[interval]) || interval}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="secondary" className="flex-1" onClick={() => setEditRuleDialog(null)}>
                  {t('actions.cancel') || 'Cancel'}
                </Button>
                <Button className="flex-1 bg-[#7097fa] text-white hover:opacity-90" onClick={handleSaveEditRule} disabled={savingRule}>
                  {savingRule ? (t('common.saving') || 'Saving...') : (t('actions.save') || 'Save')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete recurring rule confirmation */}
      <AlertDialog open={!!deleteRuleTarget} onOpenChange={(open) => !open && setDeleteRuleTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('expenses.recurring.deleteTitle') || 'Delete recurring expense?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('expenses.recurring.deleteDescription') || 'This will remove the recurring rule and delete all future expense entries created from it. Past entries will remain.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRecurringRule} className="bg-destructive text-white hover:bg-destructive/90">
              {t('actions.delete') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('expenses.filters') || 'Filters'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Category Filter - dropdown with multi-select checkmarks */}
            <div>
              <Label className="text-sm text-gray-700 mb-2 block">
                {t('expenses.filterByCategory') || 'Category'}
              </Label>
              <div className="relative">
                {/* Dropdown trigger styled like input/select */}
                <button
                  type="button"
                  onClick={() => setCategoryDropdownOpen((open) => !open)}
                  className="w-full h-10 px-3 py-2 flex items-center justify-between rounded-md border border-input bg-background text-sm"
                >
                  <span className="truncate text-left text-gray-800">
                    {selectedCategories.length === 0
                      ? (t('expenses.allCategories') || 'All categories')
                      : selectedCategories.length === 1
                        ? (() => {
                            const val = selectedCategories[0];
                            if (val === 'Service') return t('category.Service') || 'Service';
                            const cat = dbCategories.find(c => c.value === val);
                            return t(`category.${val}`) || cat?.label || val;
                          })()
                        : `${selectedCategories.length} selected`}
                  </span>
                  <span className="ml-2 text-gray-400 text-xs">▾</span>
                </button>

                {categoryDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg p-2 space-y-1">
                    {/* Service category */}
                    <label className="flex items-center justify-between text-sm cursor-pointer px-1 py-0.5 rounded hover:bg-gray-50">
                      <div className="flex items-center gap-2 min-w-0">
                        {/* Use same Repair icon as in AddServiceEntryPage */}
                        <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                          <img
                            src="/icons/AddServiceEntry/Repair.png"
                            alt={t('category.Service') || 'Service'}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="text-gray-800 truncate">
                          {t('category.Service') || 'Service'}
                        </span>
                      </div>
                      <Checkbox
                        className="h-4 w-4 rounded-[4px] border-gray-300 bg-white data-[state=checked]:bg-[#7097fa] data-[state=checked]:border-[#7097fa] data-[state=checked]:text-white"
                        checked={selectedCategories.includes('Service')}
                        onCheckedChange={() => toggleCategory('Service')}
                      />
                    </label>
                    {/* Expense categories (alphabetical) */}
                    {sortedCategories.map((cat) => (
                      <label
                        key={cat.value}
                        className="flex items-center justify-between text-sm cursor-pointer px-1 py-0.5 rounded hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {/* Category icon */}
                          {cat.icon_path ? (
                            <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                              <img
                                src={cat.icon_path}
                                alt={t(`category.${cat.value}`) || cat.label}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] text-gray-500">
                                {(t(`category.${cat.value}`) || cat.label || cat.value)
                                  .toString()
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            </div>
                          )}
                          <span className="text-gray-800 truncate">
                            {t(`category.${cat.value}`) || cat.label}
                          </span>
                        </div>
                        <Checkbox
                          className="h-4 w-4 rounded-[4px] border-gray-300 bg-white data-[state=checked]:bg-[#7097fa] data-[state=checked]:border-[#7097fa] data-[state=checked]:text-white"
                          checked={selectedCategories.includes(cat.value)}
                          onCheckedChange={() => toggleCategory(cat.value)}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Date Filter - months & years as pills */}
            <div>
              <Label className="text-sm text-gray-700 mb-2 block">
                {t('expenses.filterByDate') || 'Date'}
              </Label>

              {/* Months */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-1">
                  {t('expenses.months') || 'Months'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(
                    (label, idx) => {
                      const active = selectedMonths.includes(idx);
                      return (
                        <button
                          key={label}
                          type="button"
                          onClick={() => toggleMonth(idx)}
                          className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                            active
                              ? 'bg-[#7097fa] text-white border-[#7097fa] shadow-sm'
                              : 'bg-white text-gray-700 border-gray-300'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    }
                  )}
                </div>
              </div>

              {/* Years (only years that have data) */}
              {availableYears.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    {t('expenses.years') || 'Years'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {availableYears.map((year) => {
                      const active = selectedYears.includes(year);
                      return (
                        <button
                          key={year}
                          type="button"
                          onClick={() => toggleYearFilter(year)}
                          className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                            active
                              ? 'bg-[#7097fa] text-white border-[#7097fa] shadow-sm'
                              : 'bg-white text-gray-700 border-gray-300'
                          }`}
                        >
                          {year}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Clear & Apply Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => {
                  setSelectedCategories([]);
                  setSelectedMonths([]);
                  setSelectedYears([]);
                }}
                variant="secondary"
                className="flex-1"
              >
                {t('expenses.clearFilters') || 'Clear Filters'}
              </Button>
              <Button
                onClick={() => setFilterDialogOpen(false)}
                className="flex-1 bg-[#E7FE52] text-black hover:bg-[#E7FE52]/90"
              >
                {t('expenses.applyFilters') || 'Apply'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Dialog with Actions */}
      <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('expenses.receipt') || 'Receipt'}</DialogTitle>
          </DialogHeader>
          {photoUrl && (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={photoUrl}
                  alt="Receipt"
                  className="w-full h-auto rounded-lg border border-gray-200"
                />
              </div>
              {selectedExpense && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setPhotoDialogOpen(false);
                      handleOpenExpense(selectedExpense);
                    }}
                    variant="secondary"
                    className="flex-1"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t('actions.open') || 'Open'}
                  </Button>
                  <Button
                    onClick={() => {
                      setPhotoDialogOpen(false);
                      handleEditExpense(selectedExpense);
                    }}
                    variant="secondary"
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {t('actions.edit') || 'Edit'}
                  </Button>
                  <Button
                    onClick={() => {
                      setPhotoDialogOpen(false);
                      handleDeleteClick(selectedExpense);
                    }}
                    variant="destructive"
                    className="flex-1"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('actions.delete') || 'Delete'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirmDelete') || 'Confirm Delete'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('expenses.confirmDelete') || 'Are you sure you want to delete this expense? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {t('actions.delete') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default ExpensesListPage;

